import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { BiCheckbox } from "react-icons/bi";
import { FaBars, FaTimes } from "react-icons/fa";
import { Home } from "lucide-react"; // Changed from ArrowLeft to Home
import Swal from "sweetalert2";

function NewsPostDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  const API_BASE_URL = "https://backend.dlapakistan.com/api/member/";
  const primaryColor = "#70B444";

  // State for content
  const [content, setContent] = useState(null);
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for sidebar links (including "Become a Member" link)
  const [sidebarLinks, setSidebarLinks] = useState([]);

  // Window resize handle
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 1029) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobileView = windowWidth <= 1029;

  // Load content data
  useEffect(() => {
    loadContent();
    loadSidebarLinks();
  }, [slug]);

  const loadContent = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // First, find the post with this slug to get the button
      const postsResponse = await fetch(`${API_BASE_URL}posts/`);
      
      if (!postsResponse.ok) {
        throw new Error('Failed to fetch posts from server');
      }

      const allPosts = await postsResponse.json();
      
      // Find post with matching slug
      let foundPost = allPosts.find(p => p.slug === slug);
      
      // If not found, try by ID
      if (!foundPost && !isNaN(slug)) {
        const postByIdResponse = await fetch(`${API_BASE_URL}posts/${slug}/`);
        if (postByIdResponse.ok) {
          foundPost = await postByIdResponse.json();
        }
      }
      
      if (foundPost) {
        setPost(foundPost);
        
        // Now fetch content for this post's button
        if (foundPost.button) {
          try {
            const contentsResponse = await fetch(`${API_BASE_URL}contents/`);
            if (contentsResponse.ok) {
              const allContents = await contentsResponse.json();
              const foundContent = allContents.find(c => c.button === foundPost.button);
              if (foundContent) {
                setContent(foundContent);
              } else {
                setError("No additional content found for this button");
              }
            }
          } catch (err) {
            console.error("Error loading content:", err);
            setError("Failed to load content");
          }
        } else {
          setError("This post doesn't have a linked button");
        }
      } else {
        setError("Post not found");
      }
      
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Get sidebar links including "Become a Member" link
  const loadSidebarLinks = async () => {
    try {
      const postsResponse = await fetch(`${API_BASE_URL}posts/`);
      let allLinks = [];
      
      // Add "Become a Member" as first link
      allLinks.push({
        text: "Become a Member",
        border: "#ccc",
        showIcon: true,
        path: "/about/become-member", // Changed to this route
        isMainLink: true
      });
      
      if (postsResponse.ok) {
        const allPosts = await postsResponse.json();
        const postsWithButtons = allPosts.filter(p => p.button && p.button.trim() !== "");
        const uniqueButtons = [...new Set(postsWithButtons.map(p => p.button.trim()))];
        
        const buttonLinks = uniqueButtons.map(buttonName => {
          const postWithButton = postsWithButtons.find(p => p.button === buttonName);
          return {
            text: buttonName,
            border: "#ccc",
            showIcon: true,
            path: `/news/${postWithButton?.slug || postWithButton?.id || generateSlug(buttonName)}`,
            buttonName: buttonName,
            isMainLink: false
          };
        });
        
        allLinks = [...allLinks, ...buttonLinks];
      }
      
      setSidebarLinks(allLinks);
    } catch (err) {
      console.error("Error loading sidebar links:", err);
      // At least show "Become a Member" link
      setSidebarLinks([{
        text: "Become a Member",
        border: "#ccc",
        showIcon: true,
        path: "/about/become-member",
        isMainLink: true
      }]);
    }
  };

  // Generate slug from button name
  const generateSlug = (buttonName) => {
    if (!buttonName) return "";
    return buttonName
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/--+/g, "-")
      .trim();
  };

  // Handle sidebar click
  const handleSidebarClick = (path, item) => {
    navigate(path);
    if (isMobileView) {
      setIsSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 pt-80">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#70B444]"></div>
        <p className="ml-4 text-gray-600">Loading content...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 pt-80">
        <div className="text-red-500 text-2xl mb-4">⚠️</div>
        <p className="text-gray-600 text-xl mb-4">{error}</p>
        <button
          onClick={() => navigate("/about/become-member")}
          className="px-6 py-3 bg-[#70B444] text-white rounded-lg font-medium hover:bg-[#5d9a3a] transition-colors"
        >
          Back to Become a Member
        </button>
      </div>
    );
  }

  if (!content) {
    return null;
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 pt-40">
      
      {/* Breadcrumb */}
      <div className="w-full px-8 py-6 mt-8">
        <div className="flex flex-wrap items-center text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/")}
          >
            Home
          </span> 
          <span className="mx-2">»</span>
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/about/become-member")}
          >
            Become a Member
          </span>
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium">
            {content.title}
          </span>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="w-full px-8 md:px-12 pb-20">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative">
          {/* DESKTOP SIDEBAR */}
          {!isMobileView && sidebarLinks.length > 0 && (
            <div className="w-full lg:w-72 xl:w-80 shrink-0 bg-[#f7f9fc] p-4 md:p-5 rounded-lg shadow-sm h-fit mt-6">
              <div className="bg-[#70B444] text-white p-3 text-center mb-4">
                <h3 className="m-0 text-base lg:text-lg font-semibold">Become A Member</h3>
              </div>
              {sidebarLinks.map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleSidebarClick(item.path, item)}
                  className={`flex items-center justify-between py-2.5 font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-2 text-sm lg:text-base ${
                    item.border ? "border-b border-gray-200" : ""
                  } ${location.pathname === item.path ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444]" : ""}
                  ${item.isMainLink ? "text-[#70B444] font-bold" : "text-[#70B444]"}`}
                >
                  <div className="flex items-center">
                    {item.showIcon && <BiCheckbox className="mr-2 text-lg flex-shrink-0" />}
                    <span className="break-words">{item.text}</span>
                  </div>
                  {item.isMainLink && (
                    <Home className="text-[#70B444] text-sm" size={16} />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* MAIN CONTENT AREA - SIRF CONTENT KA TITLE AUR DESCRIPTION */}
          <div className={`flex-1 ${isMobileView ? 'p-0' : 'p-2 md:p-4'} ${
            isMobileView && isSidebarOpen ? "opacity-30 pointer-events-none" : "opacity-100"
          }`}>
            {/* Page Title - No Back Button */}
            <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-3 md:pb-4 mb-4 md:mb-6 mt-8">
              {/* Title only, no back button */}
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#70B444] leading-snug">
                {content.title}
              </h1>
              
              {isMobileView && (
                <button
                  onClick={toggleSidebar}
                  className="flex items-center gap-2 text-[#70B444] border border-[#70B444] rounded-lg px-3 py-2 hover:bg-[#70B444] hover:text-white transition-all text-sm font-semibold shrink-0"
                >
                  {isSidebarOpen ? <FaTimes /> : <FaBars />}
                  <span>{isSidebarOpen ? "Close" : "Menu"}</span>
                </button>
              )}
            </div>

            {/* CONTENT KA TITLE AUR DESCRIPTION */}
            <div className="animate-fadeIn text-left w-full bg-transparent mt-10">
              
              {/* TITLE SECTION */}
              <div className="mb-12 border-l-4 pl-6" style={{ borderColor: primaryColor }}>
                <h2 className="text-4xl font-bold text-gray-900 tracking-tight">
                  {content.title}
                </h2>
              </div>

              {/* DESCRIPTION SECTION */}
              <div className="mt-16 border-t border-gray-200 pt-12 w-full bg-transparent">
                <div 
                  className="text-gray-600 text-lg leading-loose space-y-6 custom-description w-full prose max-w-none bg-transparent" 
                  dangerouslySetInnerHTML={{ __html: content.description || "" }} 
                />
              </div>
            </div>
          </div>

          {/* MOBILE SIDEBAR */}
          {isMobileView && sidebarLinks.length > 0 && (
            <>
              <div
                className={`fixed top-0 right-0 h-full w-[85vw] max-w-sm bg-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out ${
                  isSidebarOpen ? "translate-x-0" : "translate-x-full"
                }`}
              >
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b flex justify-between items-center bg-[#70B444] text-white">
                    <span className="font-bold text-lg">Navigation</span>
                    <FaTimes 
                      className="cursor-pointer text-2xl" 
                      onClick={toggleSidebar} 
                    />
                  </div>
                  <div className="overflow-y-auto flex-1 p-2">
                    {sidebarLinks.map((item, index) => (
                      <div
                        key={index}
                        onClick={() => handleSidebarClick(item.path, item)}
                        className={`p-3 md:p-4 text-base border-b border-gray-100 flex items-center justify-between hover:bg-gray-50 ${
                          location.pathname === item.path 
                            ? "text-[#70B444] font-semibold bg-green-50 border-l-4 border-l-[#70B444]" 
                            : "text-gray-700"
                        } ${item.isMainLink ? "font-bold" : ""}`}
                      >
                        <div className="flex items-center">
                          {item.showIcon && <BiCheckbox className="mr-3 text-xl text-[#70B444] flex-shrink-0" />}
                          <span>{item.text}</span>
                        </div>
                        {item.isMainLink && (
                          <Home className="text-[#70B444]" size={18} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Overlay */}
              {isMobileView && isSidebarOpen && (
                <div
                  className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
                  onClick={toggleSidebar}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* STYLES */}
      <style jsx>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        
        .animate-fadeIn { 
          animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        /* Remove background and borders from rich text output */
        .custom-description * { 
          background-color: transparent !important; 
          border: none !important; 
        }
        
        .custom-description ul { 
          list-style-type: disc; 
          padding-left: 1.5rem; 
        }
        
        .custom-description ol { 
          list-style-type: decimal; 
          padding-left: 1.5rem; 
        }
        
        /* Prose styling with #70B444 color */
        .prose {
          --tw-prose-headings: #70B444;
          --tw-prose-body: #374151;
          --tw-prose-bold: #70B444;
          --tw-prose-links: #70B444;
          --tw-prose-code: #dc2626;
          --tw-prose-quotes: #70B444;
        }
        
        .prose h1,
        .prose h2,
        .prose h3,
        .prose h4 {
          color: #70B444;
          font-weight: 600;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
        }
        
        .prose p {
          margin-bottom: 1em;
          line-height: 1.7;
        }
        
        .prose a {
          color: #70B444;
          text-decoration: underline;
        }
        
        .prose strong {
          color: #70B444;
          font-weight: 600;
        }
        
        .prose blockquote {
          border-left: 4px solid #70B444;
          padding-left: 1em;
          margin: 1em 0;
          color: #6b7280;
          font-style: italic;
        }
        
        .prose table {
          border-collapse: collapse;
          width: 100%;
          margin-bottom: 1em;
        }
        
        .prose table th,
        .prose table td {
          border: 1px solid #e5e7eb;
          padding: 8px 12px;
          text-align: left;
        }
        
        .prose table th {
          background-color: #f9fafb;
          font-weight: 600;
          color: #111827;
        }
        
        .prose code {
          background-color: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          font-size: 0.9em;
          color: #dc2626;
        }
        
        .prose img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1em 0;
        }
      `}</style>
    </div>
  );
}

export default NewsPostDetail;